import {
  CLIENTS_PARAMS_ACCOUNT_ID_PATTERN,
  CLIENTS_PARAMS_CREATE_CONSTANTS,
  CLIENTS_PARAMS_DISPLAY_COLUMNS,
  CLIENTS_PARAMS_DISPLAY_ONLY_COLUMNS,
  CLIENTS_PARAMS_FALLBACKS,
  CLIENTS_PARAMS_LIMITS,
  CLIENTS_PARAMS_SETUP_COLUMN_TYPES,
  CLIENTS_PARAMS_TS_START_MAX_AGE_YEARS,
  tsStartMonthBounds,
  validateClientsParamsSetup,
  type ClientsParamsFrontProductColumn,
  type ClientsParamsSetupColumn,
  type ClientsParamsSetupInput,
  type ClientsParamsTaggingSector,
} from "@nexus/contracts";

// ─── New Customer setup (run_management.clients_params) ─────────────
// Written once on create, read-only afterwards. Keys are the literal
// BigQuery columns; the contracts schema owns validation and fallbacks.

export const CUSTOMER_SETUP_LIST_COLUMNS = [
  "nc_names",
  "custom_pixel",
  "google_account_ids",
  "google_custom_event",
  "nc_columns_level",
] as const;
export type CustomerSetupListColumn = (typeof CUSTOMER_SETUP_LIST_COLUMNS)[number];

export const CUSTOMER_SETUP_TEXT_COLUMNS = [
  "position_nc_sep",
  "attribution_window",
  "conversion_action",
  "adlib_url",
] as const;
export type CustomerSetupTextColumn = (typeof CUSTOMER_SETUP_TEXT_COLUMNS)[number];

export const CUSTOMER_SETUP_CHECKBOX_COLUMNS = [
  "position_nc",
  "overwrite_conversion",
  "overwrite_product",
  "low_memory_version",
] as const;
export type CustomerSetupCheckboxColumn = (typeof CUSTOMER_SETUP_CHECKBOX_COLUMNS)[number];

/**
 * Naming-convention setup, rendered together in one section in this order.
 * Presentation only: the draft keeps these in its list / text / checkbox
 * groups and the stored keys are unchanged.
 */
export const CUSTOMER_SETUP_NAMING_CONVENTION_COLUMNS = [
  "position_nc",
  "position_nc_sep",
  "nc_names",
  "nc_columns_level",
  "nc_dict",
] as const satisfies readonly ClientsParamsSetupColumn[];
export type CustomerSetupNamingConventionColumn = (typeof CUSTOMER_SETUP_NAMING_CONVENTION_COLUMNS)[number];

export function isNamingConventionColumn(column: string): column is CustomerSetupNamingConventionColumn {
  return (CUSTOMER_SETUP_NAMING_CONVENTION_COLUMNS as readonly string[]).includes(column);
}

/** One code → meaning pair of the naming convention dictionary (`nc_dict`). */
export interface NcDictRow {
  key: string;
  value: string;
}

export interface CustomerSetupDraft {
  account_id: string[];
  /** `YYYY-MM`, or "" until a month is chosen. Required: it sets how far back data is pulled, so it never defaults. */
  ts_start: string;
  tagging_sector: ClientsParamsTaggingSector | "";
  front_product_column: ClientsParamsFrontProductColumn;
  /** Edited as rows; saved as the `nc_dict` object. */
  nc_dict: NcDictRow[];
  lists: Record<CustomerSetupListColumn, string[]>;
  texts: Record<CustomerSetupTextColumn, string>;
  checkboxes: Record<CustomerSetupCheckboxColumn, boolean>;
}

export type CustomerSetupErrorField =
  | "account_id"
  | "ts_start"
  | "tagging_sector"
  | "position_nc_sep"
  | "nc_names"
  | "custom_pixel"
  | "google_account_ids"
  | "google_custom_event"
  | "conversion_action"
  | "nc_dict"
  | "adlib_url"
  | "other";
export type CustomerSetupError =
  | "required"
  | "accountIdFormat"
  | "invalidTsStart"
  | "tsStartOutOfRange"
  | "incompleteMapping"
  | "duplicateMapping"
  | "invalidUrl"
  | "customConversionId"
  | "tooLong"
  | "tooMany"
  | "invalidValue"
  | "invalid";
export type CustomerSetupErrors = Partial<Record<CustomerSetupErrorField, CustomerSetupError>>;

const META_ACCOUNT_PREFIX = "act_";
const DIGITS = /^\d+$/;

/** A Meta account ID as shown and typed: digits only, without `act_`. */
export function toMetaAccountDigits(value: string): string {
  const trimmed = value.trim();
  return trimmed.toLowerCase().startsWith(META_ACCOUNT_PREFIX) ? trimmed.slice(META_ACCOUNT_PREFIX.length) : trimmed;
}

/** The stored form, `act_<digits>`, with exactly one prefix. Anything else is kept as typed so validation flags it. */
export function toMetaAccountId(value: string): string {
  const digits = toMetaAccountDigits(value);
  return DIGITS.test(digits) ? `${META_ACCOUNT_PREFIX}${digits}` : value.trim();
}

/** Typed or pasted Meta IDs: split on commas, stored as `act_<digits>`, and `123` / `act_123` kept once. */
export function normalizeMetaAccountIds(values: readonly string[]): string[] {
  return splitListValues(values).map(toMetaAccountId).filter((id, index, ids) => ids.indexOf(id) === index);
}

/** List values as entered, with comma-separated pastes split into separate values and blanks dropped. */
export function splitListValues(values: readonly string[]): string[] {
  return values.flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean);
}

/** The Attribution Window choices, as in Retool. The combined window is one option. */
export const ATTRIBUTION_WINDOW_OPTIONS = ["7d_click", "1d_click", "1d_click,7d_click,1d_view"] as const;

/**
 * The standard Conversion Action values, as observed in production clients_params. The
 * downstream pipeline's own list has not been confirmed independently.
 */
export const CONVERSION_ACTION_OPTIONS = ["omni_purchase", "offsite_conversion.fb_pixel_purchase", "purchase"] as const;

/** A customer-specific Meta conversion: `offsite_conversion.custom.<numeric id>`. */
export const CUSTOM_CONVERSION_ACTION_PREFIX = "offsite_conversion.custom.";

/** The custom conversion ID when the value is a custom conversion (even with an ID still being typed), else null. */
export function toCustomConversionId(value: string): string | null {
  return value.startsWith(CUSTOM_CONVERSION_ACTION_PREFIX) ? value.slice(CUSTOM_CONVERSION_ACTION_PREFIX.length) : null;
}

export function toCustomConversionAction(id: string): string {
  return `${CUSTOM_CONVERSION_ACTION_PREFIX}${id.trim()}`;
}

/** The name fields the naming convention can apply to, as Retool offers them. */
export const NC_COLUMNS_LEVEL_OPTIONS = ["ad_name", "adset_name", "campaign_name"] as const;

export function createCustomerSetupDraft(): CustomerSetupDraft {
  return {
    account_id: [],
    ts_start: "",
    tagging_sector: "",
    front_product_column: CLIENTS_PARAMS_FALLBACKS.front_product_column,
    nc_dict: [],
    lists: {
      nc_names: [...CLIENTS_PARAMS_FALLBACKS.nc_names],
      custom_pixel: [...CLIENTS_PARAMS_FALLBACKS.custom_pixel],
      google_account_ids: [...CLIENTS_PARAMS_FALLBACKS.google_account_ids],
      google_custom_event: [...CLIENTS_PARAMS_FALLBACKS.google_custom_event],
      nc_columns_level: [...CLIENTS_PARAMS_FALLBACKS.nc_columns_level],
    },
    texts: {
      position_nc_sep: CLIENTS_PARAMS_FALLBACKS.position_nc_sep,
      attribution_window: CLIENTS_PARAMS_FALLBACKS.attribution_window,
      conversion_action: CLIENTS_PARAMS_FALLBACKS.conversion_action,
      adlib_url: CLIENTS_PARAMS_FALLBACKS.adlib_url,
    },
    checkboxes: { position_nc: false, overwrite_conversion: false, overwrite_product: false, low_memory_version: false },
  };
}

/** Prefilled fallbacks do not count; only the required inputs signal intent. */
export function hasCustomerSetupInput(draft: CustomerSetupDraft): boolean {
  return (
    draft.account_id.length > 0 ||
    draft.ts_start.trim().length > 0 ||
    draft.tagging_sector !== ""
  );
}

export interface ParsedCustomerSetup {
  /** The request body `setup`; null while any field is invalid. */
  setup: ClientsParamsSetupInput | null;
  errors: CustomerSetupErrors;
}

/** Mirrors the API check and ends with the contract's own `validateClientsParamsSetup`, so nothing it rejects is sent; blank optional fields are sent as null so the server applies the fallback. */
export function parseCustomerSetupDraft(draft: CustomerSetupDraft, today: Date = new Date()): ParsedCustomerSetup {
  const errors: CustomerSetupErrors = {};
  const accountIds = normalizeMetaAccountIds(draft.account_id);
  if (accountIds.length === 0) errors.account_id = "required";
  else if (!accountIds.every((id) => CLIENTS_PARAMS_ACCOUNT_ID_PATTERN.test(id))) errors.account_id = "accountIdFormat";

  const tsStart = resolveTsStart(draft.ts_start, today);
  if (!tsStart.ok) errors.ts_start = tsStart.error;

  if (!draft.tagging_sector) errors.tagging_sector = "required";

  if (draft.checkboxes.position_nc) {
    if (!draft.texts.position_nc_sep.trim()) errors.position_nc_sep = "required";
    if (!draft.lists.nc_names.some((name) => name.trim())) errors.nc_names = "required";
  }

  const customConversionId = toCustomConversionId(draft.texts.conversion_action);
  if (customConversionId !== null && !DIGITS.test(customConversionId)) errors.conversion_action = "customConversionId";

  const ncDict = toNcDict(draft.nc_dict);
  if (!ncDict.ok) errors.nc_dict = ncDict.error;
  else if (JSON.stringify(ncDict.value ?? {}).length > CLIENTS_PARAMS_LIMITS.maxJsonLength) errors.nc_dict = "tooLong";

  // The same size limits the server enforces, flagged on the field instead of as one general error.
  for (const [field, values] of [["account_id", accountIds], ...LIMITED_LIST_FIELDS.map((column) => [column, draft.lists[column]] as const)] as const) {
    if (errors[field]) continue;
    const sizeError = listSizeError(values);
    if (sizeError) errors[field] = sizeError;
  }
  for (const field of LIMITED_TEXT_FIELDS) {
    if (!errors[field] && draft.texts[field].trim().length > CLIENTS_PARAMS_LIMITS.maxTextLength) errors[field] = "tooLong";
  }

  if (Object.keys(errors).length > 0 || !tsStart.ok || !draft.tagging_sector || !ncDict.ok) {
    return { setup: null, errors };
  }

  const setup: ClientsParamsSetupInput = {
    account_id: accountIds,
    ts_start: tsStart.value,
    tagging_sector: draft.tagging_sector,
    front_product_column: draft.front_product_column,
    nc_dict: ncDict.value,
    ...draft.checkboxes,
    ...Object.fromEntries(CUSTOMER_SETUP_LIST_COLUMNS.map((column) => [column, draft.lists[column]])),
    ...Object.fromEntries(CUSTOMER_SETUP_TEXT_COLUMNS.map((column) => [column, draft.texts[column].trim() || null])),
  };
  const validation = validateClientsParamsSetup(setup, today);
  if (!validation.ok) return { setup: null, errors: contractErrorFor(validation.message) };
  return { setup, errors: {} };
}

// ─── Read-only display ──────────────────────────────────────────────

const NOT_IN_CREATE_FORM: ReadonlySet<ClientsParamsSetupColumn> = new Set([
  ...CLIENTS_PARAMS_DISPLAY_ONLY_COLUMNS,
  ...(Object.keys(CLIENTS_PARAMS_CREATE_CONSTANTS) as ClientsParamsSetupColumn[]),
]);

/** The read-only view shows only the columns New Organization asks for. */
export const CUSTOMER_SETUP_DISPLAY_COLUMNS = CLIENTS_PARAMS_DISPLAY_COLUMNS.filter(
  (column) => !NOT_IN_CREATE_FORM.has(column),
);

export type CustomerSetupDisplay =
  | { kind: "empty" }
  | { kind: "bool"; value: boolean }
  | { kind: "monthYear"; month: string; year: string }
  | { kind: "text"; text: string };

const TS_START_DATE = /^(\d{4})-(\d{2})-\d{2}$/;
const LIST_SEPARATOR = ", ";

/** Stored values are not defaulted; lists and objects are flattened to plain, comma-separated text. */
export function toCustomerSetupDisplay(column: ClientsParamsSetupColumn, value: unknown): CustomerSetupDisplay {
  if (value === null || value === undefined) return { kind: "empty" };
  const type = CLIENTS_PARAMS_SETUP_COLUMN_TYPES[column];
  if (type === "bool" && typeof value === "boolean") return { kind: "bool", value };
  if (type === "date" && typeof value === "string") {
    const match = TS_START_DATE.exec(value);
    if (match?.[1] && match[2]) return { kind: "monthYear", month: match[2], year: match[1] };
  }
  if (column === "account_id" && Array.isArray(value)) {
    const digits = value.filter((id): id is string => typeof id === "string").map(toMetaAccountDigits);
    return digits.length === 0 ? { kind: "empty" } : { kind: "text", text: digits.join(", ") };
  }
  const text = toReadableText(value);
  return text.length === 0 ? { kind: "empty" } : { kind: "text", text };
}

export function toReadableText(value: unknown): string {
  if (Array.isArray(value)) return value.map(toReadableText).join(LIST_SEPARATOR);
  if (value !== null && typeof value === "object") {
    return Object.entries(value)
      .map(([key, entry]) => `${key}: ${toReadableText(entry)}`)
      .join(LIST_SEPARATOR);
  }
  return String(value);
}

const MONTH_PATTERN = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;
const MONTHS_PER_YEAR = 12;

function toMonthKey(year: number, month: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

/**
 * The days the TS start picker offers: the first day of the month two years back through today. Only the
 * month is stored, so any day in the earliest month is allowed, and no future day.
 */
export function tsStartDateBounds(today: Date): { min: Date; max: Date } {
  return {
    min: new Date(today.getFullYear() - CLIENTS_PARAMS_TS_START_MAX_AGE_YEARS, today.getMonth(), 1),
    max: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
  };
}

/** The picker's date for a draft value: the day itself, or the first of the month for `YYYY-MM`. */
export function toTsStartPickerDate(raw: string): Date | undefined {
  const match = MONTH_PATTERN.exec(raw.trim());
  if (!match || !toTsStartMonth(raw)) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, match[3] ? Number(match[3]) : 1);
}

/** The draft value for a picked day, `YYYY-MM-DD` in local time. */
export function toTsStartDraftDay(date: Date): string {
  return `${toMonthKey(date.getFullYear(), date.getMonth() + 1)}-${String(date.getDate()).padStart(2, "0")}`;
}

/** `YYYY-MM`, or `YYYY-MM-DD` read as that day's month. Null for anything else, including month 00 or 13. */
export function toTsStartMonth(raw: string): string | null {
  const match = MONTH_PATTERN.exec(raw.trim());
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > MONTHS_PER_YEAR) return null;
  return toMonthKey(Number(match[1]), month);
}

type TsStartResult =
  | { ok: true; value: { year: number; month: number } }
  | { ok: false; error: "required" | "invalidTsStart" | "tsStartOutOfRange" };

/** Required, never defaulted. The saved value is `DATE(year, month, 1)`, so the day is always 01. */
function resolveTsStart(raw: string, today: Date): TsStartResult {
  if (raw.trim() === "") return { ok: false, error: "required" };
  const chosen = toTsStartMonth(raw);
  if (!chosen) return { ok: false, error: "invalidTsStart" };
  const { min, max } = tsStartMonthBounds(today);
  if (chosen < min || chosen > max) return { ok: false, error: "tsStartOutOfRange" };
  return { ok: true, value: { year: Number(chosen.slice(0, 4)), month: Number(chosen.slice(5, 7)) } };
}

/** Backstop for a contract rejection the field checks above did not already name. */
export function contractErrorFor(message: string): CustomerSetupErrors {
  const field = /^setup\.(\w+)/.exec(message)?.[1];
  if (field === "adlib_url") return { adlib_url: "invalidUrl" };
  if (field === "ts_start") return { ts_start: "tsStartOutOfRange" };
  if (field === "account_id") return { account_id: "accountIdFormat" };
  if (field === "position_nc_sep" || field === "nc_names") return { [field]: "required" };
  // Any other field the server names gets its error beside that field; only an unnamed failure is general.
  if (field && (FIELD_ERROR_KEYS as readonly string[]).includes(field)) return { [field]: "invalidValue" };
  return { other: "invalid" };
}

/** Setup fields that show their own error beside the input. */
const FIELD_ERROR_KEYS = [
  "tagging_sector",
  "custom_pixel",
  "google_account_ids",
  "google_custom_event",
  "conversion_action",
  "nc_dict",
] as const satisfies readonly CustomerSetupErrorField[];

/** Free-text list inputs, checked against the server's list limits. */
const LIMITED_LIST_FIELDS = ["nc_names", "custom_pixel", "google_account_ids", "google_custom_event"] as const satisfies readonly (CustomerSetupListColumn & CustomerSetupErrorField)[];

/** Free-text inputs, checked against the server's text limit. */
const LIMITED_TEXT_FIELDS = ["position_nc_sep", "conversion_action", "adlib_url"] as const satisfies readonly (CustomerSetupTextColumn & CustomerSetupErrorField)[];

function listSizeError(values: readonly string[]): CustomerSetupError | null {
  if (values.length > CLIENTS_PARAMS_LIMITS.maxListItems) return "tooMany";
  if (values.some((value) => value.trim().length > CLIENTS_PARAMS_LIMITS.maxListItemLength)) return "tooLong";
  return null;
}

type NcDictResult = { ok: true; value: Record<string, string> | null } | { ok: false; error: "incompleteMapping" | "duplicateMapping" };

/** Blank rows are ignored; a value without a code, or a repeated code, is an error. No rows send null so the server applies its fallback. */
function toNcDict(rows: readonly NcDictRow[]): NcDictResult {
  const entries: Array<[string, string]> = [];
  for (const row of rows) {
    const key = row.key.trim();
    const value = row.value.trim();
    if (!key && !value) continue;
    if (!key || !value) return { ok: false, error: "incompleteMapping" };
    entries.push([key, value]);
  }
  if (new Set(entries.map(([key]) => key)).size !== entries.length) return { ok: false, error: "duplicateMapping" };
  return { ok: true, value: entries.length > 0 ? Object.fromEntries(entries) : null };
}
